import React, { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { CommentUpdateInput } from 't2-graphql-types';
import CreateCommentForm from './CreateCommentForm';
import { createCommentMutation } from '../mutations/createCommentMutation';
import { useAuthenticatedUser } from '../../Auth/hooks/useAuthenticatedUser';
import { showNotification } from '../../NotificationBanner/showNotification';
import { useProtectWithLogin } from '../../ProtectWithLogin/hooks/useProtectWithLogin';
import { TCloudinaryImageShape } from '../../CloudinaryResponsiveImage/types';
import { useAnalytics } from '../../AnalyticsProvider/AnalyticsProvider';
import { ArticleType } from '../../Article/types';
import { TCommentOrigin } from '../types';

type TCreateCommentFormContainerProps = {
  articleId: string;
  onDiscardButtonClick?: () => void;
  handleReplyToCommentSubmit: () => void;
  isVisible?: boolean;
  showCancelButton?: boolean;
  commentId?: string;
  commentOrigin?: TCommentOrigin;
  autofocusWhenVisible?: boolean;
};

const CreateCommentFormContainer: FC<TCreateCommentFormContainerProps> = ({
  articleId,
  commentId,
  onDiscardButtonClick,
  handleReplyToCommentSubmit,
  isVisible = false,
  showCancelButton = false,
  commentOrigin,
  autofocusWhenVisible = false,
}) => {
  const { sendEvent } = useAnalytics();
  const { data, authenticatedUserId } = useAuthenticatedUser();
  const profileImage = data?.user?.profileImage;
  const { shouldProtect, loading, redirect } = useProtectWithLogin();
  const [isLoading, setIsLoading] = useState(false);
  const [createComment] = useMutation<
    Comment,
    Partial<{
      articleId: string;
      authorId: string;
      content: string;
      parentCommentId: string;
      data: CommentUpdateInput;
    }>
  >(createCommentMutation, {
    onError: () => {
      showNotification({
        title: 'Error',
        message: 'Sorry, we couldn’t publish your comment.',
        type: 'error',
      });
    },
  });

  const sendCommentFormEvent = (
    eventName: 'comment_form_submit' | 'comment_form_submit_success' | 'comment_form_submit_error',
  ) => {
    sendEvent(eventName, {
      root_item_type: ArticleType.Article,
      root_item_id: articleId,
      related_comment_id: commentId,
      related_comment_origin: commentOrigin,
      destination: 't2',
    });
  };

  const onSubmit = async (commentValue: string) => {
    sendCommentFormEvent('comment_form_submit');

    if (shouldProtect && !loading) {
      return redirect();
    }

    setIsLoading(true);

    const { errors } = await createComment({
      variables: {
        data: {
          ...(commentId && commentOrigin === 't2' ? { parentComment: { connect: { id: commentId } } } : {}),
          article: { connect: { id: articleId } },
          author: { connect: { id: authenticatedUserId } },
          content: commentValue,
        },
      },
    });

    if (!errors) {
      sendCommentFormEvent('comment_form_submit_success');
      handleReplyToCommentSubmit();
    } else {
      sendCommentFormEvent('comment_form_submit_error');
    }

    setIsLoading(false);
  };

  const handleDiscardButtonClick = () => {
    sendEvent('comment_form_discard_button_click');
    onDiscardButtonClick?.();
  };

  return (
    <CreateCommentForm
      commenterProfileImage={profileImage as TCloudinaryImageShape}
      onSubmit={onSubmit}
      isLoading={isLoading}
      onDiscardButtonClick={handleDiscardButtonClick}
      isVisible={isVisible}
      showCancelButton={showCancelButton}
      autofocusWhenVisible={autofocusWhenVisible}
    />
  );
};
export default CreateCommentFormContainer;
